import { useEffect, useState, type ReactNode } from "react";
import type { BootstrapResponse } from "../types";
import { today } from "../payrollPresentation";
import { periodRange, type PeriodSelection } from "./PeriodFilter";
import { WorkerPerformance, type PerformanceGranularity } from "./WorkerPerformance";
import { PayrollWorkspace } from "./SettlementManagement";
import "./PerformanceCard.css";
import "./SettlementConsole.css";

/**
 * 성과와 정산이 한 창에서 위아래로 선다.
 *
 * 전에는 둘이 자리를 나눠 썼다 — 훑는 값(성과)은 판 오른쪽 카드, 고르는 일(정산)은 넓은
 * 창. 같은 질문("이 사람 얼마")을 두 자리에서 답하느라 어디서 봤는지가 매번 헷갈렸다.
 *
 * 칸을 왼쪽 레일로 가르지 않는다 — 담는 것이 성과 하나와 정산 표 하나뿐이라, 레일을 세우면
 * 고르개가 고를 것보다 커진다. 위아래로 쌓으면 읽는 순서가 그대로 쓰는 순서다: 이 기간이
 * 어땠는지 보고, 그 아래에서 줄을 고른다.
 *
 * 기간은 고르지 않는다 — 둘 다 "전체"를 본다. 고르개를 두면 위아래가 같은 기간을 보는지
 * 매번 확인해야 하는데, 들고 있는 기록이 한두 해라 전체가 곧 기본이다.
 */

const ALL: PeriodSelection = { type: "all" };

export function SettlementConsoleBody({ data, actorName, mode = "all", scale, scaleControl, periodDays, periodName }: { data: BootstrapResponse; actorName: string;
  /** 설정 > 성과: 최근 며칠을 볼지(기간 세그먼트)와 그 이름(「6개월」). 문장 「…의 최근 6개월 성과는 …입니다」에 쓴다. */
  periodDays?: number; periodName?: string;
  /** 설정 > 성과: 일·주·2주·4주 세그먼트. Reflect 의 Conversations / Time spent 자리(그림 이름 줄 오른쪽)에 선다. */
  scaleControl?: ReactNode;
  /** 성과 칸 제목 줄에서 고른 집계 단위. */
  scale?: PerformanceGranularity;
  /** "performance": 설정 창의 성과 칸 — 숫자 줄과 그림만. 정산 표는 정산 칸이 따로 맡는다. */
  mode?: "all" | "performance" }) {
  const [workerId, setWorkerId] = useState("all");
  /* 기간 고르개는 두지 않는다 — 들고 있는 기록이 한두 해라 "전체"가 곧 기본이고, 하루치를
     보는 길은 그림을 끌어 짚는 것으로 이미 있다. 칩 다섯이 늘 같은 그림을 그리면 그 줄은
     누를 이유가 없는 줄이 된다. */
  const range = periodRange(mode === "performance" && periodDays ? { type: "recent", days: periodDays } : ALL, today());
  /* 2026-10-19 (사용자 결정): Claude Reflect 처럼 — 위에 큰 숫자 줄, 그 아래 이름 줄 오른쪽에
     세그먼트 하나(성과 / 정산 예정 / 정산 내역). 세그먼트가 아래 칸을 그림 ↔ 정산 표로 바꾼다. */
  const [view, setView] = useState<"chart" | "due" | "done">("chart");
  const segments = <div className="reflect-seg" role="radiogroup" aria-label="보기">
    {([["chart", "성과"], ["due", "정산 예정"], ["done", "정산 내역"]] as const).map(([value, label]) =>
      <button key={value} type="button" role="radio" aria-checked={view === value} onClick={() => setView(value)}>{label}</button>)}
  </div>;

  return <div className="settlement-console settlement-console--reflect">
    {/* 좁은 카드가 쓰던 그 모양 그대로다 — 타일 둘과 그림 하나. 평균 넷을 늘어놓는 넓은
        변형은 쓰지 않는다: 같은 값을 네 번 말하고, 그림이 ⓘ 뒤로 숨는다. 틀(.performance-card)을
        그대로 두는 이유는 그 모양을 그 클래스 아래 규칙들이 쥐고 있어서다. */}
    <section className="performance-card performance-card--pane" aria-label="성과">
      <WorkerPerformance compact data={data} range={range} selectedWorkerId={workerId}
        onSelectWorker={setWorkerId} sectionTail={mode === "performance" ? scaleControl : segments} reflectTitle={mode === "performance" ? "성과" : undefined} reflectPeriod={periodName}
        sectionTitle={{ chart: "성과 추이", due: "정산 예정", done: "정산 내역" }[view]} hideChart={view !== "chart"} compactScale={scale} />
    </section>

    {/* 예정과 내역은 표가 제 손잡이로 가른다 — 바깥에 또 하나 세우면 같은 일을 두 군데서
        한다. */}
    {mode !== "performance" && view !== "chart" && <PayrollWorkspace data={data} actorName={actorName} range={range} stage={view} onStage={setView} />}
  </div>;
}

/**
 * 판 위에 띄우는 통팝업.
 *
 * <dialog showModal> 을 쓰지 않는다 — 그것은 뒤를 통째로 inert 로 만들어, 창이 떠 있는
 * 동안 오른쪽 위 연장줄(작업자·성과)이 죽는다. 성과가 카드로 살던 때는 그 줄로 바로 옮겨
 * 탈 수 있었고, 창이 되었다고 그 길이 막히면 창을 닫는 일이 먼저가 된다. 그래서 뒤를
 * 가리되 막지는 않는다: 어두운 바탕은 한 번 누르면 걷히고, 연장줄만 그 위에 남는다.
 */
export function SettlementConsole({ data, actorName, onClose }: {
  data: BootstrapResponse; actorName: string; onClose: () => void;
}) {
  useEffect(() => {
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [onClose]);

  return <div className="settlement-console-layer">
    {/* 바탕은 가리기만 한다. 누르면 걷힌다 — 바깥을 눌러 닫는 길은 창과 같다. */}
    <div className="settlement-console-scrim" onClick={onClose} aria-hidden="true" />
    <section className="settlement-console-window" role="dialog" aria-modal="false" aria-label="정산">
      <header className="settlement-console-head">
        <h2>정산</h2>
        <button type="button" className="settlement-console-close" aria-label="닫기" onClick={onClose}>
          <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="m6.8 6.8 10.4 10.4M17.2 6.8 6.8 17.2" /></svg>
        </button>
      </header>
      <div className="settlement-console-body">
        <SettlementConsoleBody data={data} actorName={actorName} />
      </div>
    </section>
  </div>;
}
